// News wire — bottom-of-screen panel in the alliance-panel visual family.
// Mac System 7 outer chrome (`.bolo-panel` + `.bolo-panel-inner`) with an
// inner header band (orange uppercase title + green-pip LIVE chip) sitting
// over a two-line horizontal marquee.
//
// The marquee mirrors WinBolo's behaviour (see
// references/winbolo/winbolo/src/bolo/messages.c and messages.h): two
// monospace lines that scroll left in lockstep:
//   - Top line  = per-message sender header (e.g. a player nick for chat;
//                 blank for system events because the chrome already says
//                 "NEWS WIRE").
//   - Bottom    = message body.
//
// We diverge from WinBolo's "Newswire" auto-prefix because our chrome
// header band identifies the panel persistently — printing "Newswire"
// again at the front of every system message would be redundant. The top
// line is reserved for future per-sender labels (chat with player nicks).
//
// Buffer width: WinBolo hard-coded MESSAGE_WIDTH=68 (messages.h:43) to
// match its fixed-pixel window. Our panel is responsive — its pixel
// width depends on the viewport — so the buffer is sized at construction
// (and on resize) to the number of monospace columns that actually fit
// in the marquee. That keeps text entering from the right edge on any
// display.
//
// Timing: WinBolo advances ONE COLUMN per scroll tick at
// MESSAGE_SCROLL_TIME=4 game ticks. Bolo's sim runs at 50 Hz so one
// scroll step = 4/50 s = 80 ms. Each event is enqueued as a sequence of
// per-column (top-char, bottom-char) pairs, padded with a trailing space
// so back-to-back events don't run into each other.
//
// Idle: when the queue is empty, blank columns shift in and old text
// scrolls off the left edge until the wire is empty.

const STEP_MS = 80; // MESSAGE_SCROLL_TIME=4 at 50 Hz sim → 80 ms/column
const MIN_WIDTH = 20; // floor for very narrow viewports

interface Column {
  top: string;
  bottom: string;
}

export class NewsWire {
  private topEl: HTMLElement;
  private bottomEl: HTMLElement;
  private topBuf: string[];
  private botBuf: string[];
  private queue: Column[] = [];
  private lastSource: string | null = null;
  private width: number;
  // Interval handle — null when the marquee is idle (queue empty AND both
  // buffers all-blank) so we don't tick forever writing blank lines.
  // enqueue() restarts it.
  private timer: number | null = null;
  // Last strings written to the DOM — render() skips the textContent
  // assignment (and the style/layout invalidation it causes) when unchanged.
  private lastTopText: string | null = null;
  private lastBottomText: string | null = null;

  constructor(root: HTMLElement) {
    root.classList.add('news-wire-body');

    // Header band — same visual primitives as #ally-panel .ally-header.
    const header = document.createElement('div');
    header.className = 'news-wire-header';
    const title = document.createElement('span');
    title.className = 'news-wire-title';
    title.textContent = 'News wire';
    const chip = document.createElement('span');
    chip.className = 'news-wire-chip';
    const pip = document.createElement('span');
    pip.className = 'news-wire-pip';
    const chipLabel = document.createElement('span');
    chipLabel.className = 'news-wire-chip-label';
    chipLabel.textContent = 'Live';
    chip.append(pip, chipLabel);
    header.append(title, chip);

    // Marquee — two-line scroll buffer below the header.
    const marquee = document.createElement('div');
    marquee.className = 'news-wire-marquee';
    this.topEl = document.createElement('div');
    this.topEl.className = 'news-wire-line news-wire-top';
    this.bottomEl = document.createElement('div');
    this.bottomEl.className = 'news-wire-line news-wire-bottom';
    marquee.append(this.topEl, this.bottomEl);

    root.append(header, marquee);

    this.width = this.measureColumns();
    this.topBuf = new Array(this.width).fill(' ');
    this.botBuf = new Array(this.width).fill(' ');

    window.addEventListener('resize', () => this.handleResize());

    this.enqueue('', 'Press ? for keyboard controls');
    this.render();
  }

  // System events have no per-message sender — the chrome header band
  // already identifies the panel, so the marquee's top line stays blank.
  add(text: string): void {
    this.enqueue('', text);
  }

  // Push an event with an explicit sender label on the top line (e.g.
  // a player nick for chat). Reserved for future use; not yet wired up.
  addWithSource(source: string, text: string): void {
    this.enqueue(source, text);
  }

  // How many monospace columns fit in one line of the marquee right now.
  // Measures a 100-char probe inserted into the actual top-line element so
  // it inherits the live font + size; the line's bounding rect gives the
  // pixel width available for text.
  private measureColumns(): number {
    const probe = document.createElement('span');
    probe.style.visibility = 'hidden';
    probe.style.whiteSpace = 'pre';
    probe.style.position = 'absolute';
    probe.textContent = '0'.repeat(100);
    this.topEl.appendChild(probe);
    const charWidth = probe.getBoundingClientRect().width / 100;
    probe.remove();
    const lineWidth = this.topEl.getBoundingClientRect().width;
    if (!charWidth || !lineWidth) return MIN_WIDTH;
    return Math.max(MIN_WIDTH, Math.floor(lineWidth / charWidth));
  }

  private handleResize(): void {
    const next = this.measureColumns();
    if (next === this.width) return;
    if (next > this.width) {
      // Grow: pad new blank cells on the LEFT, keeping current content
      // anchored to the right edge (the entry point for new chars).
      const extra = next - this.width;
      this.topBuf = new Array(extra).fill(' ').concat(this.topBuf);
      this.botBuf = new Array(extra).fill(' ').concat(this.botBuf);
    } else {
      // Shrink: drop oldest cells on the LEFT (those nearest exiting).
      this.topBuf = this.topBuf.slice(this.topBuf.length - next);
      this.botBuf = this.botBuf.slice(this.botBuf.length - next);
    }
    this.width = next;
    this.render();
  }

  private enqueue(source: string, body: string): void {
    // Trailing space separates back-to-back events visually.
    const padded = body + ' ';
    // Header suppression (messages.c:122-134): same source as the last
    // event → top line stays blank for this body's run, so the previous
    // header continues to apply. Empty string is its own "no-source"
    // bucket — consecutive system events all suppress.
    const showSource = source !== '' && source !== this.lastSource;
    const sourceText = showSource ? source : '';
    this.lastSource = source;
    const len = Math.max(sourceText.length, padded.length);
    for (let i = 0; i < len; i++) {
      this.queue.push({
        top: sourceText[i] ?? ' ',
        bottom: padded[i] ?? ' ',
      });
    }
    // Wake the marquee if it went idle. No-op while already ticking, so a
    // message enqueued mid-scroll just joins the queue on the same cadence.
    this.ensureTicking();
  }

  private ensureTicking(): void {
    if (this.timer !== null) return;
    this.timer = window.setInterval(() => this.tick(), STEP_MS);
  }

  private tick(): void {
    this.topBuf.shift();
    this.botBuf.shift();
    const next = this.queue.shift() ?? { top: ' ', bottom: ' ' };
    this.topBuf.push(next.top);
    this.botBuf.push(next.bottom);
    this.render();
    // Idle stop: nothing queued and both lines fully blank — every further
    // tick would be a visual no-op, so park the interval until the next
    // enqueue(). The render above already painted the final all-blank state.
    if (this.timer !== null && this.queue.length === 0 && this.isBlank()) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  private isBlank(): boolean {
    for (const c of this.topBuf) if (c !== ' ') return false;
    for (const c of this.botBuf) if (c !== ' ') return false;
    return true;
  }

  private render(): void {
    const top = this.topBuf.join('');
    const bottom = this.botBuf.join('');
    if (top !== this.lastTopText) {
      this.topEl.textContent = top;
      this.lastTopText = top;
    }
    if (bottom !== this.lastBottomText) {
      this.bottomEl.textContent = bottom;
      this.lastBottomText = bottom;
    }
  }
}
